/**
 * One song in step 2's picker.
 *
 * §9 asks that the music come from the REAL Music Library and that no fake audio files be
 * created. Both are honoured by this row being a view of an actual `Song` record from
 * songsStore — nothing is fabricated for the Publisher, and the songs listed are the same
 * ones the Library shows.
 *
 * THE AUDIO WARNING IS THE IMPORTANT PART. §9: "if no real audio asset exists, prevent
 * submission and clearly explain why". Every Lyrixa song currently has `audioUrl: null`
 * because generation is simulated, so this warning is on every row — and the row is now
 * DISABLED as well. The earlier version let the user pick an undeliverable track and only
 * blocked them later, which left a control on screen that looked available and was not.
 * Disabling the row makes the rule visible at the moment of choice instead of after it.
 *
 * The explanation comes from api/publisherApi.describeAudioBlocker, so the sentence here
 * and the sentence in the refusal are the same sentence.
 */
import { AlertTriangle, Check } from 'lucide-react';
import { SongArtwork } from '../songs';
import { describePublisherSongBlocker } from '../../api/publisherApi';
import { joinWithMiddleDot } from '../../lib/formatting';
import { isUploadedSong } from '../../lib/license';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

export interface SongPickRowProps {
  song: Song;
  checked: boolean;
  onToggle: (songId: string) => void;
  selectionBlocker?: string;
  /** Position on the release, 1-based, when selected. Shown so track order is visible. */
  position?: number;
}

export default function SongPickRow({
  song,
  checked,
  onToggle,
  selectionBlocker,
  position,
}: SongPickRowProps) {
  const publisherBlocker = describePublisherSongBlocker(song);
  // A legacy draft may already contain an ineligible song. Keep that checked row
  // removable while preventing it from ever being selected again.
  const disabled = Boolean(publisherBlocker || selectionBlocker) && !checked;

  return (
    <label
      className="lyx-pick"
      aria-disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        border: `1px solid ${checked ? c.accentBorder : c.border}`,
        background: c.surface2,
        borderRadius: 9,
        padding: '9px 11px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.72 : 1,
      }}
    >
      {/*
        A real checkbox, visually hidden but focusable — the pattern RadioCard uses, for
        the same reason: the tick below is the design, and a click-only <div> would be
        unreachable by keyboard and unannounced by a screen reader. `lyx-visually-hidden`
        is the app's existing class for this.
      */}
      <input
        type="checkbox"
        className="lyx-visually-hidden"
        checked={checked}
        onChange={() => onToggle(song.id)}
        disabled={disabled}
        aria-label={`${checked ? 'Remove' : 'Add'} ${song.title}`}
      />
      <span
        aria-hidden="true"
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          border: `1.5px solid ${checked ? c.accentFill : c.borderStrong}`,
          background: checked ? c.accentFill : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {checked && <Check size={11} color="#1A1300" />}
      </span>

      <SongArtwork
        song={song}
        height={38}
        radius={6}
        style={{ width: 38, flexShrink: 0, overflow: 'hidden' }}
      />

      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: 'block',
            fontSize: 12.5,
            color: c.text1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {position ? `${position}. ` : ''}
          {song.title}
        </span>
        <span style={{ display: 'block', fontSize: 11, color: c.text2 }}>
          {joinWithMiddleDot([song.genre, song.length, song.language])}
        </span>
        {(publisherBlocker || selectionBlocker) && (
          <span
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 5,
              marginTop: 4,
              fontSize: 11,
              color: c.text2,
              lineHeight: 1.45,
            }}
          >
            <AlertTriangle size={11} color={c.accent} style={{ flexShrink: 0, marginTop: 2 }} />
            {isUploadedSong(song)
              ? publisherBlocker
              : publisherBlocker
                ? 'No audio file yet, so this track cannot be delivered.'
                : selectionBlocker}
          </span>
        )}
      </span>
    </label>
  );
}
